import { reactive, computed } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import SiteAdminService from '@/services/SiteAdminService'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'

/**
 * Draft / publish / preview state of one site, shared by every component
 * that shows it (site header bar, Overview card, Theme sticky bar) so a draft
 * save on the Theme tab updates the "N unpublished changes" pill at once.
 * Contract: internal/sites-draft-publish-contract.md (runtime drafts, castr
 * publish + preview). Everything degrades: a runtime without drafts answers
 * no `draftCount` → the indicator is hidden and saves publish immediately;
 * a castr without `/preview` → a toast.
 */
const states = new Map()

function stateFor(siteId) {
	if (!states.has(siteId)) {
		states.set(siteId, reactive({
			// null = unknown (not loaded yet / older runtime); number otherwise
			draftCount: null,
			draftKeys: [],
			draftUpdatedAt: null,
			loaded: false,
			refreshing: false,
			publishing: false,
			discarding: false,
			previewing: false,
			// last minted preview link, reused while it is still valid
			preview: null,
			// bumps after every publish / discard / draft save so preview panes reload
			version: 0,
		}))
	}
	return states.get(siteId)
}

export function useSiteDraft(siteId) {
	const store = useStore()
	const { notify } = useNotification()
	const id = () => (typeof siteId === 'function' ? siteId() : siteId?.value ?? siteId)
	const state = computed(() => stateFor(id()))

	const site = computed(() => store.getters['Sites/siteById'](id()))
	const isDraftSite = computed(() => site.value?.status === 'draft')
	const draftSupported = computed(() => typeof state.value.draftCount === 'number')
	const draftCount = computed(() => (draftSupported.value ? state.value.draftCount : 0))
	const hasDraft = computed(() => draftCount.value > 0)

	/** Copy the draft fields of a runtime `GET`/`PATCH /settings` answer. */
	function applySettingsResponse(res) {
		const s = state.value
		if (!res || typeof res !== 'object') return
		if (typeof res.draftCount === 'number') {
			s.draftCount = res.draftCount
			s.draftKeys = Object.keys(res.draft || {})
			s.draftUpdatedAt = res.draftUpdatedAt || null
		} else if (res.draft && typeof res.draft === 'object') {
			s.draftKeys = Object.keys(res.draft)
			s.draftCount = s.draftKeys.length
		}
		s.loaded = true
	}

	/** Re-read the runtime settings (cheap; castr caches the draft count 10 s). */
	async function refresh() {
		const s = state.value
		if (s.refreshing) return null
		s.refreshing = true
		try {
			const res = await SiteAdminService.getSettings(id())
			applySettingsResponse(res)
			// older runtime: no draft fields at all → drafts unsupported
			if (typeof res?.draftCount !== 'number' && !res?.draft) s.draftCount = null
			s.loaded = true
			return res
		} catch {
			// castr's toPublic carries a best-effort count; keep whatever we had
			if (typeof site.value?.draftCount === 'number') s.draftCount = site.value.draftCount
			return null
		} finally {
			s.refreshing = false
		}
	}

	/** Mark a successful `?target=draft` save so preview panes reload. */
	function noteDraftSaved(res) {
		applySettingsResponse(res)
		state.value.version += 1
	}

	/**
	 * castr POST /sites/{id}/publish: runtime draft → live, `draft` site →
	 * `active`, KV republished. Refreshes the store's site afterwards.
	 */
	async function publish() {
		const s = state.value
		if (s.publishing) return null
		s.publishing = true
		try {
			const res = await store.dispatch('Sites/publishSite', id())
			s.draftCount = draftSupported.value ? 0 : s.draftCount
			s.draftKeys = []
			s.version += 1
			const n = res.publishedKeys?.length || 0
			trackSites(SITES_EVENTS.SITE_PUBLISHED, res.site || id(), { published_changes: n })
			const runtimeSkipped = res.runtime?.status === 'skipped'
			notify({
				group: 'success',
				text: n
					? `Published ${n} ${n === 1 ? 'change' : 'changes'}. Viewers see them within a minute.`
					: runtimeSkipped ? 'Site published. Settings drafts are not supported by the runtime yet.' : 'Site published. Viewers see it within a minute.',
			})
			return res
		} catch (err) {
			notify({ group: 'error', text: err.message || 'Could not publish the site.' })
			throw err
		} finally {
			s.publishing = false
		}
	}

	/** Runtime DELETE /draft: throws the unpublished settings away. */
	async function discard() {
		const s = state.value
		if (s.discarding) return null
		s.discarding = true
		try {
			const res = await SiteAdminService.discardDraft(id())
			s.draftCount = 0
			s.draftKeys = []
			s.version += 1
			notify({ group: 'success', text: `Discarded ${res?.discarded ?? ''} unpublished ${res?.discarded === 1 ? 'change' : 'changes'}.`.replace(/\s+/g, ' ') })
			return res
		} catch (err) {
			notify({ group: 'error', text: err.message || 'Could not discard the draft.' })
			throw err
		} finally {
			s.discarding = false
		}
	}

	/**
	 * A preview link is valid for ≤ 1 h; mint a new one when the cached one
	 * has < 2 minutes left.
	 * @return {Promise<{ url: string, expires_at: string, host?: string }|null>}
	 */
	async function getPreview({ force = false } = {}) {
		const s = state.value
		const cached = s.preview
		if (!force && cached && new Date(cached.expires_at).getTime() - Date.now() > 120_000) return cached
		s.previewing = true
		try {
			s.preview = await SiteAdminService.createPreview(id())
			return s.preview
		} catch (err) {
			s.preview = null
			const text = err?.code === 404
				? 'Preview links are not available on this environment yet.'
				: err?.reason === 'sites_admin_unconfigured'
					? 'Preview is not configured on the server yet.'
					: err.message || 'Could not create a preview link.'
			notify({ group: 'error', text })
			return null
		} finally {
			s.previewing = false
		}
	}

	/**
	 * Open the preview in a new tab. The tab is opened synchronously (popup
	 * blockers) and pointed at the minted URL afterwards; `noopener` would
	 * return null here, so the opener link is cut by hand instead.
	 */
	async function openPreview() {
		const win = typeof window !== 'undefined' ? window.open('', '_blank') : null
		if (win) win.opener = null
		const preview = await getPreview()
		if (!preview?.url) {
			win?.close()
			return null
		}
		if (win) win.location.replace(preview.url)
		else window.open(preview.url, '_blank', 'noopener')
		trackSites(SITES_EVENTS.PREVIEW_OPENED, id())
		return preview
	}

	return {
		state,
		site,
		isDraftSite,
		draftSupported,
		draftCount,
		hasDraft,
		refresh,
		applySettingsResponse,
		noteDraftSaved,
		publish,
		discard,
		getPreview,
		openPreview,
	}
}
